import { useState, useEffect } from "react";
import { Calendar, UserCheck, Clock, Shield } from "lucide-react";
import { getMatches, getTeams, getPlayers } from "../lib/api";

export default function MatchOfficials() {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, assigned, unassigned

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [matchesRes, teamsRes, playersRes] = await Promise.all([
        getMatches(),
        getTeams(),
        getPlayers(),
      ]);
      setMatches(matchesRes.data);
      setTeams(teamsRes.data);
      setPlayers(playersRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTeamName = (teamId) => {
    const team = teams.find((t) => t.id === teamId);
    return team?.name || "Unknown";
  };

  const getTeamLogo = (teamId) => {
    const team = teams.find((t) => t.id === teamId);
    return team?.logo;
  };

  const getOfficialName = (officialId) => {
    const player = players.find((p) => p.id === officialId);
    if (!player) return "Unknown";
    const team = teams.find((t) => t.id === player.team_id);
    return `${player.name} (${team?.name || "?"})`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "No date";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr; // Return original if invalid
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "--:--";
    return timeStr; // Already in HH:MM format
  };

  // Helper to get match datetime for comparison
  const getMatchDateTime = (match) => {
    const dateStr = match.match_date || "1970-01-01";
    const timeStr = match.match_time || "00:00";
    return new Date(`${dateStr}T${timeStr}`);
  };

  // Filter matches based on selection
  const filteredMatches = matches.filter((match) => {
    const hasOfficials = match.referees && match.referees.length > 0;
    if (filter === "assigned") return hasOfficials;
    if (filter === "unassigned") return !hasOfficials;
    return true;
  });

  // Separate upcoming and past matches
  const now = new Date();
  const upcomingMatches = filteredMatches
    .filter((match) => getMatchDateTime(match) >= now)
    .sort((a, b) => getMatchDateTime(a) - getMatchDateTime(b));
  
  const pastMatches = filteredMatches
    .filter((match) => getMatchDateTime(match) < now)
    .sort((a, b) => getMatchDateTime(b) - getMatchDateTime(a)); // Most recent first

  // Group upcoming matches by date
  const groupedUpcoming = upcomingMatches.reduce((groups, match) => {
    const date = match.match_date || "Unknown";
    if (!groups[date]) groups[date] = [];
    groups[date].push(match);
    return groups;
  }, {});

  // Group past matches by date
  const groupedPast = pastMatches.reduce((groups, match) => {
    const date = match.match_date || "Unknown";
    if (!groups[date]) groups[date] = [];
    groups[date].push(match);
    return groups;
  }, {});

  const sortedUpcomingDates = Object.keys(groupedUpcoming).sort();
  const sortedPastDates = Object.keys(groupedPast).sort((a, b) => b.localeCompare(a)); // Most recent first

  // Render a single match card
  const renderMatchCard = (match) => {
    const hasOfficials = match.referees?.length > 0;
    const centerRef = match.referees?.find((r) => r.role === "Center Referee");
    const assistant = match.referees?.find((r) => r.role === "Assistant");
    const delegate = match.referees?.find((r) => r.role === "Delegate");

    return (
      <div
        key={match.id}
        className={`bg-card border rounded-xl p-4 ${
          hasOfficials ? "border-border" : "border-amber-500/50"
        }`}
        data-testid={`match-official-card-${match.id}`}
      >
        {/* Match Header: Time + Teams */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 mb-4">
          {/* Time */}
          <div className="flex items-center gap-2 text-muted-foreground shrink-0">
            <Clock className="w-4 h-4" />
            <span className="font-mono text-sm">{formatTime(match.match_time)}</span>
          </div>

          {/* Teams */}
          <div className="flex items-center gap-3 flex-1">
            <div className="flex items-center gap-2 flex-1 justify-end">
              <span className="font-semibold text-sm sm:text-base truncate">
                {getTeamName(match.home_team_id)}
              </span>
              {getTeamLogo(match.home_team_id) ? (
                <img
                  src={getTeamLogo(match.home_team_id)}
                  alt=""
                  className="w-8 h-8 rounded-lg object-cover shrink-0"
                />
              ) : (
                <Shield className="w-8 h-8 text-muted-foreground shrink-0" />
              )}
            </div>
            <span className="text-muted-foreground font-bold">vs</span>
            <div className="flex items-center gap-2 flex-1">
              {getTeamLogo(match.away_team_id) ? (
                <img
                  src={getTeamLogo(match.away_team_id)}
                  alt=""
                  className="w-8 h-8 rounded-lg object-cover shrink-0"
                />
              ) : (
                <Shield className="w-8 h-8 text-muted-foreground shrink-0" />
              )}
              <span className="font-semibold text-sm sm:text-base truncate">
                {getTeamName(match.away_team_id)}
              </span>
            </div>
          </div>

          {/* Status Badge */}
          <div className="shrink-0">
            {hasOfficials ? (
              <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                Assigned
              </span>
            ) : (
              <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                Unassigned
              </span>
            )}
          </div>
        </div>

        {/* Officials */}
        <div className="grid sm:grid-cols-3 gap-3 pt-3 border-t border-border">
          {/* Center Referee */}
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${centerRef ? "bg-green-500" : "bg-muted"}`}></div>
            <div>
              <div className="text-xs text-muted-foreground uppercase">Center Referee</div>
              <div className="text-sm font-medium truncate">
                {centerRef ? getOfficialName(centerRef.referee_id) : "Not assigned"}
              </div>
            </div>
          </div>

          {/* Assistant */}
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${assistant ? "bg-green-500" : "bg-muted"}`}></div>
            <div>
              <div className="text-xs text-muted-foreground uppercase">Assistant</div>
              <div className="text-sm font-medium truncate">
                {assistant ? getOfficialName(assistant.referee_id) : "Not assigned"}
              </div>
            </div>
          </div>

          {/* Delegate */}
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${delegate ? "bg-green-500" : "bg-muted"}`}></div>
            <div>
              <div className="text-xs text-muted-foreground uppercase">Delegate</div>
              <div className="text-sm font-medium truncate">
                {delegate ? getOfficialName(delegate.referee_id) : "Not assigned"}
              </div>
            </div>
          </div>
        </div>

        {/* Venue if available */}
        {match.venue && (
          <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
            📍 {match.venue}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const assignedCount = matches.filter((m) => m.referees?.length > 0).length;
  const unassignedCount = matches.length - assignedCount;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight">
          Match Officials
        </h1>
        <p className="text-muted-foreground mt-1">
          Overview of official assignments for all matches
        </p>
      </div>

      {/* Stats & Filter */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span>{assignedCount} Assigned</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
            <span>{unassignedCount} Unassigned</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              filter === "all"
                ? "bg-primary text-primary-foreground"
                : "bg-secondary hover:bg-secondary/80"
            }`}
            data-testid="filter-all"
          >
            All
          </button>
          <button
            onClick={() => setFilter("assigned")}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              filter === "assigned"
                ? "bg-primary text-primary-foreground"
                : "bg-secondary hover:bg-secondary/80"
            }`}
            data-testid="filter-assigned"
          >
            Assigned
          </button>
          <button
            onClick={() => setFilter("unassigned")}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              filter === "unassigned"
                ? "bg-primary text-primary-foreground"
                : "bg-secondary hover:bg-secondary/80"
            }`}
            data-testid="filter-unassigned"
          >
            Unassigned
          </button>
        </div>
      </div>

      {/* Matches List */}
      {upcomingMatches.length === 0 && pastMatches.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-8 text-center">
          <UserCheck className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No matches found</p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* UPCOMING MATCHES */}
          {upcomingMatches.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-bold uppercase text-primary flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Upcoming Matches ({upcomingMatches.length})
              </h2>
              <div className="space-y-6">
                {sortedUpcomingDates.map((date) => (
                  <div key={date} className="space-y-3">
                    {/* Date Header */}
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm font-semibold uppercase">
                        {formatDate(date)}
                      </span>
                      <span className="text-xs">
                        ({groupedUpcoming[date].length} match
                        {groupedUpcoming[date].length !== 1 ? "es" : ""})
                      </span>
                    </div>

                    {/* Matches for this date */}
                    <div className="grid gap-3">
                      {groupedUpcoming[date]
                        .sort((a, b) => (a.match_time || "").localeCompare(b.match_time || ""))
                        .map((match) => renderMatchCard(match))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PAST MATCHES */}
          {pastMatches.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-bold uppercase text-muted-foreground flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Past Matches ({pastMatches.length})
              </h2>
              <div className="space-y-6 opacity-75">
                {sortedPastDates.map((date) => (
                  <div key={date} className="space-y-3">
                    {/* Date Header */}
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm font-semibold uppercase">
                        {formatDate(date)}
                      </span>
                      <span className="text-xs">
                        ({groupedPast[date].length} match
                        {groupedPast[date].length !== 1 ? "es" : ""})
                      </span>
                    </div>

                    {/* Matches for this date */}
                    <div className="grid gap-3">
                      {groupedPast[date]
                        .sort((a, b) => (b.match_time || "").localeCompare(a.match_time || ""))
                        .map((match) => renderMatchCard(match))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
